import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import MarketplaceShell, { C, SERIF, card, pill } from "./MarketplaceShell";

/**
 * Trust &amp; safety, at /trust — built from the Claude Design file
 * `Trust & Safety.dc.html`.
 *
 * <p><b>Every check described here actually runs.</b> The ABN and its verification date come off the
 * business record and are published so a visitor can check the ABR themselves; the response rate is
 * withheld below a volume threshold rather than shown as a small-sample number; jobs won is null at
 * zero rather than rendered as "0"; featured placements are labelled as paid.</p>
 *
 * <p><b>And it is equally explicit about what we do NOT check</b> — the design gives that its own
 * card, at the same weight as the checks. A trust page that implies vetting nobody performs is worse
 * than no trust page: it transfers risk onto a reader who has been told they are covered.</p>
 *
 * <p>The earlier version carried a "needs your review" block asking for a response time and a
 * removal policy. The design answers both by committing to the narrower, true thing — "we review
 * every report and can remove a listing" — with no timeframe attached, so the block is gone rather
 * than bolted onto a design that did not ask for it.</p>
 */

const CHECKS = [
  {
    title: "Every listing belongs to a paying member",
    body: "Nobody is scraped into this directory or added without their knowledge. A business is here because it signed up and maintains its own profile.",
  },
  {
    title: "ABNs are published, not just claimed",
    body: "A business's ABN appears on its profile so you can look it up on the ABR yourself. Where we've confirmed it, the profile shows when that was — and where we haven't, it doesn't pretend otherwise.",
  },
  {
    title: "Council coverage is real",
    body: "A business appears in a council's results because it nominated that area, so a search returns people who work where your project is rather than everyone on the platform.",
  },
];

const NON_CHECKS = [
  {
    title: "We're a directory, not a vetting service",
    body: "We do not inspect anyone's work, verify trade licences or insurance, check references, or supervise the job.",
  },
  {
    title: "We're not a party to your agreement",
    body: "Quotes, contracts, payment, timelines and disputes are between you and the business — DAconnect doesn't hold your money or take a cut.",
  },
];

const NUMBERS: { icon: string; title: string; body: string }[] = [
  {
    icon: "⏱",
    title: "Response rate",
    body: "Worked out from enquiries actually sent through DAconnect. We don't show it at all until a business has handled enough of them for the figure to mean something — a blank is not a bad score, it's too small a sample.",
  },
  {
    icon: "✓",
    title: "Jobs won",
    body: 'Counts enquiries the business marked as won. It\'s hidden at zero rather than shown as "0", because a new member hasn\'t failed at anything.',
  },
  {
    icon: "★",
    title: "Featured listings are paid",
    body: "A business can buy a week at the top of the marketplace. Those are labelled as promoted everywhere they appear — being featured says nothing about quality, and we won't let it look like it does.",
  },
];

function TickIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.link} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b3261e" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** A card heading with its icon tile — green for the checks, red for the limits. */
function CardHead({ icon, tint, children }: { icon: ReactNode; tint: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          background: tint,
          display: "grid",
          placeItems: "center",
          flex: "none",
        }}
      >
        {icon}
      </span>
      <h2 style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 500, color: C.ink, margin: 0 }}>
        {children}
      </h2>
    </div>
  );
}

/** One rule, with the coloured rule down its left edge. */
function Point({
  title,
  body,
  rule,
  titleColor,
}: {
  title: string;
  body: string;
  rule: string;
  titleColor: string;
}) {
  return (
    <div style={{ borderLeft: `2px solid ${rule}`, paddingLeft: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: titleColor, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: C.bodyMuted, lineHeight: 1.65 }}>{body}</div>
    </div>
  );
}

export default function PartnerNetworkTrust() {
  return (
    <MarketplaceShell
      metaTitle="Trust & safety"
      metaDescription="What DAconnect checks before a business appears in the marketplace, what it doesn't, and how to report a problem."
      eyebrow="TRUST & SAFETY"
      title={
        <>
          {/* Mint, not deep green: the accent sits on a deep-green wash over the photograph, and
              deep-on-deep is the one pairing in this palette that disappears. */}
          What we check, what we don't — <b style={{ color: C.mint, fontWeight: 600 }}>plainly</b>.
        </>
      }
      intro="We'd rather be plain about the limits than imply a guarantee we don't provide."
      heroImage={{ src: "/images/partner-network/trust-hero.jpg" }}
      showBack
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))",
          gap: 24,
          alignItems: "stretch",
        }}
      >
        {/* WHAT WE CHECK */}
        <section data-testid="trust-checks" style={{ ...card, padding: "30px 32px" }}>
          <CardHead icon={<TickIcon />} tint={C.tint}>
            What we check
          </CardHead>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {CHECKS.map((c) => (
              <Point key={c.title} title={c.title} body={c.body} rule={C.mint} titleColor={C.deep} />
            ))}
          </div>
        </section>

        {/* WHAT WE DON'T CHECK */}
        <section data-testid="trust-limits" style={{ ...card, padding: "30px 32px" }}>
          <CardHead icon={<CrossIcon />} tint="#fdf2f1">
            What we don't check
          </CardHead>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {NON_CHECKS.map((c) => (
              <Point key={c.title} title={c.title} body={c.body} rule="#f0d5d3" titleColor={C.ink} />
            ))}
          </div>
          <div
            style={{
              marginTop: 18,
              background: C.ground,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              padding: "14px 16px",
              fontSize: 13,
              color: C.bodyMuted,
              lineHeight: 1.6,
            }}
          >
            <b style={{ color: C.deep }}>Before you engage anyone:</b> ask for their licence number and
            check it with the relevant state regulator, ask for a certificate of currency for their
            insurance, and get the quote in writing. That's good practice with anyone, found here or
            not.
          </div>
        </section>
      </div>

      {/* WHAT THE NUMBERS MEAN */}
      <section data-testid="trust-numbers" style={{ ...card, marginTop: 24, padding: "30px 34px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 500, color: C.ink, margin: 0 }}>
            What the numbers on a profile mean
          </h2>
          <span style={{ flex: 1, height: 1, background: C.line }} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            gap: "20px 32px",
            marginTop: 16,
          }}
        >
          {NUMBERS.map((n) => (
            <div key={n.title}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 9,
                    background: C.tint,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    flex: "none",
                  }}
                >
                  {n.icon}
                </span>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: C.ink, margin: 0 }}>{n.title}</h3>
              </div>
              <div style={{ fontSize: 13.5, color: C.bodyMuted, lineHeight: 1.65 }}>{n.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* REPORTING */}
      <section
        data-testid="trust-reporting"
        style={{
          background: C.deep,
          color: "#fff",
          borderRadius: 20,
          marginTop: 24,
          padding: "30px 34px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
          gap: "20px 40px",
          boxShadow: "0 20px 50px rgba(20,83,45,.18)",
        }}
      >
        <div
          style={{
            gridColumn: "1/-1",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2 style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 500, margin: 0 }}>
              Reporting a problem
            </h2>
            <div style={{ fontSize: 13.5, color: C.mintInk, marginTop: 4 }}>
              Inaccurate listing, impersonation, or a business behaving badly — tell us the business
              name and what happened.
            </div>
          </div>
          {/* The design draws this as a span. Contact is where a report is actually received —
              its form has "A problem with a listing" among its topics. */}
          <Link
            to="/contact"
            className="dcx-pill"
            style={{ ...pill, background: C.mint, color: C.deep, flex: "none" }}
          >
            Report a listing
          </Link>
        </div>

        <div style={{ borderLeft: "2px solid rgba(134,239,172,.4)", paddingLeft: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.mint, marginBottom: 4 }}>
            What we can do
          </div>
          <div style={{ fontSize: 13, color: C.mintInk, lineHeight: 1.65 }}>
            We review every report and can remove a listing from the marketplace.
          </div>
        </div>
        <div style={{ borderLeft: "2px solid rgba(134,239,172,.4)", paddingLeft: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.mint, marginBottom: 4 }}>
            What we can't
          </div>
          <div style={{ fontSize: 13, color: C.mintInk, lineHeight: 1.65 }}>
            We can't arbitrate a commercial dispute or recover money — for that, your state's consumer
            affairs body or fair trading office.
          </div>
        </div>
      </section>
    </MarketplaceShell>
  );
}
